import React from "react";
import type { PackageFocus } from "../../types";
import type { EffortBand, PackageChip, RequirementsPackage as PackageModel } from "./useRequirementsPackage";

interface RequirementsPackageProps {
  pkg: PackageModel;
  focus: PackageFocus | undefined;
}

// 0.55, not lower — at 0.35 an un-focused block of chips reads as disabled
// rather than de-emphasised, and the point is that the whole package stays
// legible while one part is highlighted.
const DIMMED = 0.55;

const CHIP_TONE: Record<PackageChip["tone"], string> = {
  solid: "bg-abis-primary/10 text-abis-primary border-abis-primary/20",
  plain: "bg-gray-50 text-gray-600 border-gray-200",
  warn: "bg-abis-warning/10 text-abis-warning border-abis-warning/30",
  ok: "bg-abis-success/10 text-abis-success border-abis-success/30",
};

const BAND_COLOR: Record<EffortBand, string> = {
  1: "bg-abis-success",
  2: "bg-abis-primary",
  3: "bg-abis-warning",
  4: "bg-abis-error",
};

function Chips({ chips, empty }: { chips: PackageChip[]; empty: string }) {
  if (chips.length === 0) {
    return <p className="text-[11px] text-gray-400 italic leading-snug">{empty}</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip, index) => (
        <span
          key={`${chip.label}-${index}`}
          className={`inline-block rounded-md border px-2 py-0.5 text-[11px] font-medium leading-snug ${CHIP_TONE[chip.tone]}`}
        >
          {chip.label}
        </span>
      ))}
    </div>
  );
}

function Block({
  title,
  zone,
  focus,
  children,
}: {
  title: string;
  /** undefined = never dimmed (the Aufwandsindikator summarises every section). */
  zone?: PackageFocus;
  focus: PackageFocus | undefined;
  children: React.ReactNode;
}) {
  const emphasised = zone === undefined || focus === undefined || focus === zone;
  return (
    <div
      className="border-t border-gray-100 pt-2.5 first:border-t-0 first:pt-0 transition-opacity duration-300"
      style={{ opacity: emphasised ? 1 : DIMMED }}
    >
      <h4
        className={`text-[11px] font-bold uppercase tracking-wide mb-1.5 transition-colors duration-300 ${
          zone !== undefined && focus === zone ? "text-abis-accent" : "text-gray-500"
        }`}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}

/**
 * The live Anforderungspaket: what the answers add up to, expressed as the
 * artefacts we would actually build. Decorative in the accessibility tree — every
 * value in it is derived from a form field the user just filled in, and is
 * available as text there.
 */
export default function RequirementsPackage({ pkg, focus }: RequirementsPackageProps) {
  return (
    <div aria-hidden="true" className="space-y-2.5">
      {/* First block: everything downstream is planned for this group. */}
      <Block title="Zielgruppe" zone="persona" focus={focus}>
        <Chips chips={pkg.persona} empty="Nutzergruppe und Hürden in Abschnitt 02." />
      </Block>

      <Block title="Seitenstruktur" zone="pages" focus={focus}>
        <Chips chips={pkg.pages} empty="Themenbereiche in Abschnitt 03." />
      </Block>

      <Block title="Visual-Bausteine" zone="visuals" focus={focus}>
        <Chips chips={pkg.visuals} empty="Ergibt sich aus den Abschnitten 04 und 05." />
      </Block>

      <Block title="Datenanbindung" zone="data" focus={focus}>
        <div className="space-y-1.5">
          <Chips chips={pkg.sources} empty="Quellen in Abschnitt 06." />
          {pkg.dataRows.length > 0 ? (
            <dl className="space-y-0.5">
              {pkg.dataRows.map((row) => (
                <div key={row.label} className="flex items-baseline justify-between gap-3 text-[11px]">
                  <dt className="text-gray-500 flex-shrink-0">{row.label}</dt>
                  <dd className="font-semibold text-gray-800 text-right">{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </Block>

      <Block title="Verteilung & Sichtbarkeit" zone="ops" focus={focus}>
        <Chips chips={pkg.delivery} empty="Ergibt sich aus den Abschnitten 02 und 07." />
      </Block>

      <Block title="Aufwandsindikator" focus={focus}>
        <div className="flex gap-1 mb-1.5">
          {[1, 2, 3, 4].map((segment) => {
            const filled = pkg.effort.band !== null && segment <= pkg.effort.band;
            return (
              <span
                key={segment}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                  filled && pkg.effort.band ? BAND_COLOR[pkg.effort.band] : "bg-gray-100"
                }`}
              />
            );
          })}
        </div>
        <div className="flex items-baseline justify-between gap-3 text-[11px]">
          <span className="text-gray-500">
            {pkg.effort.scored ? "Einstufung" : "Noch keine Einschätzung"}
          </span>
          <b className="font-semibold text-gray-800">{pkg.effort.bandName}</b>
        </div>
      </Block>
    </div>
  );
}
